/**
 * ScoreDeltaChip - small accent pill showing a Guardian Score delta.
 *
 * Hidden when delta is null/undefined or below the rounding-zero noise
 * threshold (|delta| < 0.05). Format: "+0.5 GS" / "-0.5 GS" up to 9.9,
 * "+12 GS" / "-12 GS" at 10+.
 *
 * Tone is sign-driven: positive deltas use the soft-green pill from
 * PillarShell's TREND_PILL_CLASS; negative deltas use the matching red
 * variant (also borrowed from PillarShell). Priority-action callers always
 * pass positive lifts, so they keep their existing green appearance with
 * no caller changes.
 */

const NOISE_THRESHOLD = 0.05
const INTEGER_BOUNDARY = 10

/**
 * Format a delta into the chip string. Exported for unit-test reuse.
 *
 *    0.5   -> "+0.5 GS"
 *   -0.4   -> "-0.4 GS"
 *    9.95  -> "+10 GS"   (rounded into integer band)
 *  -12.34  -> "-12 GS"
 *
 * Returns null when the chip should be hidden.
 */
export function formatScoreDelta(delta: number | null | undefined): string | null {
  if (delta == null) return null
  if (Math.abs(delta) < NOISE_THRESHOLD) return null
  const abs = Math.abs(delta)
  const magnitude = abs >= INTEGER_BOUNDARY
    ? Math.round(abs).toString()
    : abs.toFixed(1)
  const sign = delta < 0 ? '-' : '+'
  return `${sign}${magnitude} GS`
}

export interface ScoreDeltaChipProps {
  /** Guardian Score delta in percentage points. Null/undefined or near-zero
   *  values render nothing. Positive = green, negative = red. */
  delta: number | null | undefined
  /** Optional override of the default tooltip copy, primarily for testing. */
  tooltipOverride?: string
}

export default function ScoreDeltaChip({ delta, tooltipOverride }: ScoreDeltaChipProps) {
  const label = formatScoreDelta(delta)
  if (label == null || delta == null) return null

  // One-decimal display for the tooltip so "0.50" doesn't surface as "1"
  // even when the chip itself uses the integer band.
  const points = Math.abs(delta).toFixed(Math.abs(delta) >= INTEGER_BOUNDARY ? 0 : 1)
  // The lift is arithmetic on the framework weighted average — driven by
  // the policy's weight in its framework, not by its SHALL/SHOULD tier.
  // Tooltip frames it conversationally so two policies of similar weight
  // (one SHALL, one SHOULD) reading as the same lift doesn't surprise.
  const tooltip = tooltipOverride ?? (
    delta < 0
      ? `Your Guardian Score dropped ~${points} points. The math is weighted by each policy's framework weight, not its SHALL/SHOULD tier.`
      : `Closing this nudges your Guardian Score up by ~${points} points. The number comes from this policy's weight in the framework, so a SHOULD and a SHALL of similar weight can show the same lift.`
  )

  const toneClass = delta < 0
    ? 'bg-red-500/15 text-red-600 dark:text-red-400'
    : 'bg-primary-500/20 text-primary-700 dark:text-primary-400'

  return (
    <span
      className={`
        inline-flex items-center rounded-full px-2 py-0.5
        ${toneClass}
        text-[10px] font-bold uppercase tracking-[0.08em]
        tabular-nums whitespace-nowrap
      `}
      title={tooltip}
      aria-label={tooltip}
    >
      {label}
    </span>
  )
}
